import React, { useEffect, useState } from "react";
import { X, Plus, Copy } from "lucide-react";
import Button from "@/components/ui/Button";
import TextField from "@/components/ui/Textfield";
import DatePickerField from "@/components/ui/DatePickerField";
import Dropdown from "@/components/ui/DropDown";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import useGlobalStore from "@/store/globalStore";
import { isAlreadyRestored } from "@/api/restore";
import Spinner from "@/components/ui/Spinner";
import z, { ZodUUID } from "zod";
import CampaignDetailWrapper from "./campaign-detail-wrapper";
import {
  flightsSchema,
  UpdateFlightSchema,
} from "../validation/flight.validator";
import { isUuid } from "@/helper/utils";
import useCampaignStore, { FlightRequest } from "../store/campaignStore";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/Dialog";

interface FlightRow {
  id: string;
  language: string;
  objective: string;
  region: string;
  adCategories: string[];
}

interface Flight {
  id: string;
  inMarketDateFrom: string;
  inMarketDateTo: string;
  rows: FlightRow[];
}

const FlightSetup: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [isUpdate, setIsUpdate] = useState(false);

  const [flights, setFlights] = useState<Flight[]>([
    {
      id: "flight_1",
      inMarketDateFrom: "",
      inMarketDateTo: "",
      rows: [
        {
          id: "flight_1_1",
          language: "",
          objective: "",
          region: "",
          adCategories: [],
        },
      ],
    },
  ]);

  // Id of the saved flight awaiting delete confirmation, if any.
  const [flightPendingDelete, setFlightPendingDelete] = useState<string | null>(
    null
  );
  const [isDeletingFlight, setIsDeletingFlight] = useState(false);

  const addRowToFlight = (flightId: string) => {
    setFlights(
      flights.map((flight) =>
        flight.id === flightId
          ? {
              ...flight,
              rows: [
                ...flight.rows,
                {
                  id: `${flightId}_${flight.rows.length + 1}`,
                  language: "",
                  objective: "",
                  region: "",
                  adCategories: [],
                },
              ],
            }
          : flight
      )
    );
  };

  const removeRowFromFlight = (flightId: string, rowId: string) => {
    setFlights(
      flights.map((flight) =>
        flight.id === flightId
          ? {
              ...flight,
              rows: flight.rows.filter((row) => row.id !== rowId),
            }
          : flight
      )
    );
  };

  const updateFlightRow = (
    flightId: string,
    rowId: string,
    field: keyof FlightRow,
    value: any | ((prev: any) => any)
  ) => {
    setFlights((prevFlights) =>
      prevFlights.map((flight) =>
        flight.id === flightId
          ? {
              ...flight,
              rows: flight.rows.map((row) =>
                row.id === rowId
                  ? {
                      ...row,
                      [field]:
                        typeof value === "function" ? value(row[field]) : value,
                    }
                  : row
              ),
            }
          : flight
      )
    );
  };

  const toggleAdCategory = (
    flightId: string,
    rowId: string,
    category: string
  ) => {
    setFlights(
      flights.map((flight) =>
        flight.id === flightId
          ? {
              ...flight,
              rows: flight.rows.map((row) => {
                if (row.id === rowId) {
                  const newCategories = row.adCategories.includes(category)
                    ? row.adCategories.filter((cat) => cat !== category)
                    : [...row.adCategories, category];
                  return { ...row, adCategories: newCategories };
                }
                return row;
              }),
            }
          : flight
      )
    );
  };

  const addNewFlight = () => {
    const newFlight: Flight = {
      id: `flight_${flights.length + 1}`,
      inMarketDateFrom: "",
      inMarketDateTo: "",
      rows: [
        {
          id: `flight_${flights.length + 1}`,
          language: "",
          objective: "",
          region: "",
          adCategories: [],
        },
      ],
    };
    setFlights([...flights, newFlight]);
  };

  const duplicatePreviousFlight = () => {
    if (flights.length > 0) {
      const lastFlight = flights[flights.length - 1];
      const duplicatedFlight: Flight = {
        id: `flight_${flights.length + 1}`,
        inMarketDateFrom: "",
        inMarketDateTo: "",
        rows: lastFlight.rows.map((row, index) => ({
          ...row,
          id: `flight_${flights.length + 1}_${index + 1}`,
        })),
      };
      setFlights([...flights, duplicatedFlight]);
    }
  };

  // A saved flight carries its server UUID; one the user just added still has a
  // local `flight_N` id, so the id shape is what tells the two apart.
  const isPersistedFlight = (flightId: string) => isUuid(flightId);

  // Removing a saved flight only from local state left it in the database, so
  // the delete now goes through the API (behind a confirmation) first.
  const performDeleteFlight = async (flightId: string) => {
    const wasPersisted = isPersistedFlight(flightId);
    if (wasPersisted) {
      await deleteFlightRequest(flightId);
    }
    setFlights((prev) => prev.filter((flight) => flight.id !== flightId));

    // Only a saved flight can be undone — an unsaved one never reached the
    // server, so there is nothing to restore.
    if (wasPersisted) {
      showSuccess("Flight deleted.", undefined, {
        label: "Undo",
        onClick: () => handleUndoDeleteFlight(flightId),
      });
    }
  };

  // Undo of a just-completed delete, run straight from the toast. The page
  // rebuilds its flight rows from the API, so the restored flight reappears via
  // the same fetch the page loads with. A 404 NO_RESTORABLE_DELETION means it is
  // already back (not a failure); a 409 names the ancestor to restore first.
  const handleUndoDeleteFlight = async (flightId: string): Promise<void> => {
    try {
      await restoreFlightRequest(flightId);
      await fetchOptions();
      showSuccess("Flight restored.");
    } catch (error: any) {
      if (isAlreadyRestored(error)) {
        await fetchOptions();
        showInfo("That flight has already been restored.");
        return;
      }
      setError(error?.message ?? "Failed to restore flight");
    }
  };

  const confirmDeleteFlight = async () => {
    if (!flightPendingDelete || isDeletingFlight) return;

    setIsDeletingFlight(true);
    try {
      await performDeleteFlight(flightPendingDelete);
      setFlightPendingDelete(null);
    } catch (error: any) {
      setError(error?.message ?? "Failed to delete flight");
    } finally {
      setIsDeletingFlight(false);
    }
  };

  const deleteFlight = (flightId: string) => {
    if (isPersistedFlight(flightId)) {
      setFlightPendingDelete(flightId);
      return;
    }
    // Nothing was ever saved for this flight, so there is nothing to confirm.
    void performDeleteFlight(flightId);
  };

  const {
    objectiveOptions,
    adCategoryOptions,
    getObjectivesForFlight,
    getAdCategoryForFlight,
    createFlight,
    getFlightData,
    updateFlight,
    deleteFlightRequest,
    restoreFlightRequest,
  } = useCampaignStore(
    useShallow((state) => ({
      objectiveOptions: state.objectiveOptions,
      adCategoryOptions: state.adCategoryOptions,
      getObjectivesForFlight: state.getObjectivesForFlight,
      getAdCategoryForFlight: state.getAdCategoryForFlight,
      createFlight: state.createFlight,
      getFlightData: state.getFlightData,
      updateFlight: state.updateFlight,
      deleteFlightRequest: state.deleteFlight,
      restoreFlightRequest: state.restoreFlight,
    }))
  );
  const campaign = useCampaignStore((state) => state.campaign);
  const [isOptionsLoading, setIsOptionsLoading] = useState(false);
  const { setError, showSuccess, showInfo } = useGlobalStore(
    useShallow((state) => ({
      setError: state.setError,
      showSuccess: state.showSuccess,
      showInfo: state.showInfo,
    }))
  );
  const navigate = useNavigate();

  const fetchOptions = async () => {
    setIsOptionsLoading(true);
    try {
      const flight_data = await getFlightData(searchParams.get("id"));
      const flight_output = flight_data.map((flight, flightIndex) => ({
        id: flight.id,
        inMarketDateFrom: flight.start_date.slice(0, 10),
        inMarketDateTo: flight.end_date.slice(0, 10),
        rows: flight.rows.map((row) => ({
          id: row.id,
          language: row.language_id,
          objective: row.objective_id,
          region: row.region,
          adCategories: row.ad_category.map((cat) => cat.ad_category_id),
        })),
      }));
      if (flight_data.length != 0) {
        setIsUpdate(true);
        setFlights(flight_output);
      }

      await getObjectivesForFlight();
      await getAdCategoryForFlight();
    } catch (err: any) {
      setError(err?.message || "Something went wrong");
    } finally {
      setIsOptionsLoading(false);
    }
  };

  const saveData = async () => {
    try {
      if (isUpdate) {
        const payload = flights.map((flight, fIndex) => {
          const flightObj: any = {
            ...(isUuid(flight.id) ? { id: flight.id } : null),
            flight_number: fIndex + 1,
            start_date: flight.inMarketDateFrom || null,
            end_date: flight.inMarketDateTo || null,
            campaign_id: searchParams.get("id"),
            rows: flight.rows.map((row, rIndex) => {
              const rowObj: any = {
                ...(isUuid(row.id) ? { id: row.id } : null),
                region: row.region || null,
                ad_category: row.adCategories.length ? row.adCategories : null,
                flight_row_number: rIndex + 1,
                language_id: row.language,
                objective_id: row.objective,
              };
              return rowObj;
            }),
          };
          return flightObj;
        });

        UpdateFlightSchema.parse({
          flights: payload,
        });

        await updateFlight(
          {
            flights: payload,
          },
          searchParams.get("id")
        );

        showSuccess("Flight updated Successfully");
      } else {
        const payload: FlightRequest[] = flights.map((flight, index) => ({
          campaign_id: campaign.campaign.id,
          flight_number: index + 1,
          start_date: flight.inMarketDateFrom || null,
          end_date: flight.inMarketDateTo || null,
          rows: flight.rows.map((row, rowIndex) => ({
            flight_row_number: rowIndex + 1,
            language_id: row.language || null,
            objective_id: row.objective || null,
            region: row.region || null,
            ad_category: row.adCategories.length ? row.adCategories : null,
          })),
        }));

        flightsSchema.parse(payload);

        await createFlight({
          flights: payload,
        });
        showSuccess("Flight created Successfully");
      }
      navigate(`/campaign/assets?id=${searchParams.get("id")}`);
    } catch (err: unknown) {
      if (err instanceof z.ZodError) {
        err.issues.forEach((issue) => {
          console.warn(
            `Field: ${issue.path.join(".")}, Error: ${issue.message}`
          );
        });

        const firstIssue = err.issues[0];
        setError(firstIssue?.message || "Validation failed");
      } else {
        setError((err as Error).message || "Something went wrong");
      }
    }
  };

  useEffect(() => {
    fetchOptions();
  }, []);

  if (!campaign) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <CampaignDetailWrapper>
      {flights.map((flight, flightIndex) => (
        <div key={flight.id} className=" mb-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h3 className="text-xl font-semibold text-gray-900">
                Flight {flightIndex + 1}
              </h3>
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-gray-700">
                  In Market Date
                </span>
                <DatePickerField
                  value={flight.inMarketDateFrom}
                  onChange={(date) =>
                    setFlights(
                      flights.map((f) =>
                        f.id === flight.id
                          ? { ...f, inMarketDateFrom: date }
                          : f
                      )
                    )
                  }
                  max={flight.inMarketDateTo}
                  rangeEnd={flight.inMarketDateTo}
                  placeholder="MM/DD/YYYY"
                />
                <span className="text-sm text-gray-500">to</span>
                <DatePickerField
                  value={flight.inMarketDateTo}
                  onChange={(date) =>
                    setFlights(
                      flights.map((f) =>
                        f.id === flight.id
                          ? { ...f, inMarketDateTo: date }
                          : f
                      )
                    )
                  }
                  min={flight.inMarketDateFrom}
                  rangeEnd={flight.inMarketDateFrom}
                  placeholder="MM/DD/YYYY"
                />
              </div>
            </div>
            {flights.length > 1 && (
              <button
                onClick={() => deleteFlight(flight.id)}
                className="text-gray-400 hover:text-red-500 transition-colors flex"
              >
                <X size={16} className="mt-0.5" />
                <span className="ml-1 text-sm">Delete Flight</span>
              </button>
            )}
          </div>

          <hr className="mt-2 border-gray-300 mb-5" />

          <div className="space-y-4">
            <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-700">
              <span className="col-span-1">Language</span>
              <span className="col-span-2">Objective</span>
              <span className="col-span-3">Target</span>
              <span className="col-span-2">Ad Categories</span>
              <span className="col-span-3"></span>
            </div>

            {flight.rows.map((row) => (
              <div key={row.id} className="grid grid-cols-12 gap-4 items-start">
                <div className="col-span-1">
                  <Dropdown
                    selected={row.language}
                    setSelected={(e) =>
                      updateFlightRow(
                        flight.id,
                        row.id,
                        "language",
                        e.toString()
                      )
                    }
                    options={campaign.languages.map((e) => ({
                      value: e.language_id,
                      title: e.name,
                    }))}
                    disabled={isUpdate && isUuid(row.id)}
                  />
                </div>

                {isOptionsLoading ? (
                  <Spinner />
                ) : (
                  <div className="col-span-2">
                    <Dropdown
                      selected={row.objective}
                      setSelected={(e) =>
                        updateFlightRow(
                          flight.id,
                          row.id,
                          "objective",
                          e.toString()
                        )
                      }
                      options={objectiveOptions.map((e) => ({
                        value: e.id,
                        title: e.name,
                      }))}
                      disabled={isUpdate && isUuid(row.id)}
                    />
                  </div>
                )}

                <div className="col-span-3">
                  <TextField
                    type="text"
                    placeholder={row.region || "(Optional)"}
                    value={row.region}
                    onChange={(e) =>
                      updateFlightRow(
                        flight.id,
                        row.id,
                        "region",
                        e.target.value
                      )
                    }
                    className="w-full"
                  />
                </div>

                {isOptionsLoading ? (
                  <Spinner />
                ) : (
                  <div className="col-span-2">
                    <Dropdown
                      selected={""}
                      setSelected={(e) =>
                        updateFlightRow(
                          flight.id,
                          row.id,
                          "adCategories",
                          (prev: string[]) => [...prev, e.toString()]
                        )
                      }
                      options={adCategoryOptions
                        .filter((e) => !row.adCategories.includes(e.id))
                        .map((e) => ({
                          value: e.id,
                          title: e.name,
                        }))}
                    />
                  </div>
                )}

                <div className="flex flex-wrap gap-2 col-span-3">
                  {row.adCategories.length === 0 ? (
                    <span className="text-gray-400 text-sm text-center w-full">
                      No ad categories
                    </span>
                  ) : (
                    row.adCategories.map((category) => (
                      <span
                        key={category}
                        className="border inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full cursor-pointer"
                        onClick={() =>
                          toggleAdCategory(flight.id, row.id, category)
                        }
                      >
                        {adCategoryOptions.find((e) => e.id === category)?.name}
                        <X size={14} className="hover:text-red-500" />
                      </span>
                    ))
                  )}
                </div>
                {flight.rows.length > 1 && (
                  <button
                    onClick={() => removeRowFromFlight(flight.id, row.id)}
                    className="text-gray-400 hover:text-red-500 ml-2 col-span-"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            ))}

            <Button
              type="secondary"
              leadingIcon={<Plus size={16} />}
              onClick={() => addRowToFlight(flight.id)}
            >
              Add Row
            </Button>
          </div>
        </div>
      ))}

      <hr className="mt-2 border-gray-300 mb-3" />

      <div className="flex gap-4 mb-8">
        <Button
          type="secondary"
          leadingIcon={<Plus size={16} />}
          onClick={addNewFlight}
        >
          Add New Flight
        </Button>

        <Button
          type="secondary"
          onClick={duplicatePreviousFlight}
          leadingIcon={<Copy size={16} />}
        >
          Duplicate Previous Flight
        </Button>
      </div>

      <Button
        type="primary"
        onClick={() => {
          saveData();
        }}
      >
        Save Changes
      </Button>

      {/* Confirmation for deleting a saved flight. `bg-white` is required:
          DialogContent defaults to `bg-background`, which Tailwind never
          generates here, so the panel would render transparent. */}
      <Dialog
        open={!!flightPendingDelete}
        onOpenChange={(next) => {
          if (isDeletingFlight) return;
          if (!next) setFlightPendingDelete(null);
        }}
      >
        <DialogContent
          showCloseButton={!isDeletingFlight}
          className="sm:max-w-lg bg-white"
        >
          <DialogHeader>
            <DialogTitle>
              Delete Flight{" "}
              {flights.findIndex((f) => f.id === flightPendingDelete) + 1}?
            </DialogTitle>
            <DialogDescription>
              This removes the flight, all of its assets, versions, and stored
              media from the campaign. The records and media are retained for 30
              days, and an export of everything deleted is emailed to you.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="justify-end gap-2 mt-2">
            <Button
              type="secondary"
              onClick={() => setFlightPendingDelete(null)}
              disabled={isDeletingFlight}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={confirmDeleteFlight}
              isLoading={isDeletingFlight}
            >
              Delete flight
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CampaignDetailWrapper>
  );
};

export default FlightSetup;
